import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from 'react-bootstrap';
import { createLogger } from '../utils/debugLogger';

const logger = createLogger('detachableCalculator', 'Floating calculator widget for quick math', false, 'DEBUG');

// Evaluate a basic arithmetic expression (digits, + - * / and parentheses) using a
// small recursive-descent parser instead of eval()/Function() constructors.
const evaluateArithmetic = (expr) => {
  let i = 0;
  const peek = () => expr[i];
  const fail = () => { throw new Error('Invalid expression'); };

  const parseFactor = () => {
    if (peek() === '(') {
      i++;
      const value = parseExpr();
      if (peek() !== ')') fail();
      i++;
      return value;
    }
    const match = /^(\d+\.?\d*|\.\d+)/.exec(expr.slice(i));
    if (!match) fail();
    i += match[0].length;
    return Number(match[0]);
  };

  const parseUnary = () => {
    if (peek() === '+') { i++; return parseUnary(); }
    if (peek() === '-') { i++; return -parseUnary(); }
    return parseFactor();
  };

  const parseTerm = () => {
    let value = parseUnary();
    while (peek() === '*' || peek() === '/') {
      const op = expr[i++];
      const rhs = parseUnary();
      value = op === '*' ? value * rhs : value / rhs;
    }
    return value;
  };

  const parseExpr = () => {
    let value = parseTerm();
    while (peek() === '+' || peek() === '-') {
      const op = expr[i++];
      const rhs = parseTerm();
      value = op === '+' ? value + rhs : value - rhs;
    }
    return value;
  };

  const result = parseExpr();
  if (i !== expr.length) fail();
  return result;
};

const DetachableCalculator = ({ onClose }) => {
  // Position on the right side by default, with localStorage support
  const [position, setPosition] = useState(() => {
    // Try to load saved position from localStorage
    const savedPosition = localStorage.getItem('calculatorPosition');
    if (savedPosition) {
      try {
        return JSON.parse(savedPosition);
      } catch (e) {
        logger.error('Error parsing saved calculator position:', e);
      }
    }
    // Default position in middle-right, offset from both clock and calendar
    return { x: window.innerWidth - 500, y: 80 };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const calculatorRef = useRef(null);
  const inputRef = useRef(null);

  // Calculator state
  const [input, setInput] = useState('0');
  const [result, setResult] = useState('');
  const [hasFocus, setHasFocus] = useState(false);
  const [shouldResetInput, setShouldResetInput] = useState(false);
  const [waitingForSecondOperand, setWaitingForSecondOperand] = useState(false);

  // Auto-focus when calculator is mounted
  useEffect(() => {
    if (calculatorRef.current) {
      calculatorRef.current.focus();
    }
  }, []);

  // Handle mouse down event to start dragging
  const handleMouseDown = (e) => {
    if (e.target.closest('.close-btn') || e.target.closest('.calc-button')) return;
    
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  // Handle mouse move event when dragging
  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;

    const newPosition = {
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    };

    setPosition(newPosition);
  }, [isDragging, dragStart]);

  // Handle mouse up event to stop dragging
  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      // Save position to localStorage when dragging stops
      localStorage.setItem('calculatorPosition', JSON.stringify(position));
    }
    setIsDragging(false);
  }, [isDragging, position]);

  // Add/remove event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Save position to localStorage when component unmounts
  useEffect(() => {
    return () => {
      localStorage.setItem('calculatorPosition', JSON.stringify(position));
    };
  }, [position]);

  // Focus and blur handlers
  const handleFocus = () => {
    setHasFocus(true);
  };

  const handleBlur = () => {
    setHasFocus(false);
  };

  // Calculator functions
  const inputDigit = useCallback((digit) => {
    if (shouldResetInput) {
      setInput(digit);
      setShouldResetInput(false);
    } else if (waitingForSecondOperand) {
      setInput(input + digit);
      setWaitingForSecondOperand(false);
    } else {
      setInput(input === '0' ? digit : input + digit);
    }
  }, [shouldResetInput, waitingForSecondOperand, input]);

  const inputDecimal = useCallback(() => {
    if (shouldResetInput) {
      setInput('0.');
      setShouldResetInput(false);
      return;
    }

    if (waitingForSecondOperand) {
      setInput(input + '0.');
      setWaitingForSecondOperand(false);
      return;
    }

    // Check if the current number being entered already has a decimal point
    const parts = input.split(/[+\-*/]/);
    const currentNumber = parts[parts.length - 1];

    if (!currentNumber.includes('.')) {
      setInput(input + '.');
    }
  }, [shouldResetInput, waitingForSecondOperand, input]);

  const inputParenthesis = useCallback((parenthesis) => {
    if (shouldResetInput) {
      setInput(parenthesis);
      setShouldResetInput(false);
      return;
    }

    if (waitingForSecondOperand) {
      setInput(input + parenthesis);
      setWaitingForSecondOperand(false);
    } else {
      if (input === '0') {
        setInput(parenthesis);
      } else {
        setInput(input + parenthesis);
      }
    }
  }, [shouldResetInput, waitingForSecondOperand, input]);

  const handleBackspace = useCallback(() => {
    if (shouldResetInput || waitingForSecondOperand) {
      return;
    }

    if (input !== '0') {
      if (input.length === 1) {
        setInput('0');
      } else {
        setInput(input.slice(0, -1));
      }
    }
  }, [shouldResetInput, waitingForSecondOperand, input]);

  const clearInput = useCallback(() => {
    setInput('0');
    setResult('');
    setWaitingForSecondOperand(false);
    setShouldResetInput(false);
  }, []);

  const handleOperator = useCallback((nextOperator) => {
    // If we already have a result and are starting a new calculation
    if (shouldResetInput) {
      setInput(input + nextOperator);
      setShouldResetInput(false);
    } else {
      // Add the operator to the equation
      setInput(input + nextOperator);
    }

    setWaitingForSecondOperand(false);
  }, [shouldResetInput, input]);

  const safeEval = useCallback((expression) => {
    try {
      // Replace all instances of 'x' with '*' for multiplication
      expression = expression.replace(/×/g, '*');
      expression = expression.replace(/÷/g, '/');

      // Evaluate using the recursive-descent parser above (no eval()/Function())
      return evaluateArithmetic(expression);
    } catch (e) {
      logger.error('Error evaluating expression:', e);
      return 'Error';
    }
  }, []);

  const performCalculation = useCallback(() => {
    if (input === '0') {
      return;
    }

    try {
      const calculationResult = safeEval(input);

      if (isNaN(calculationResult) || !isFinite(calculationResult)) {
        setResult('Error');
      } else {
        // Format the result - if it's an integer, don't show decimal places
        const formattedResult = Number.isInteger(calculationResult)
          ? calculationResult.toString()
          : calculationResult.toFixed(8).replace(/\.?0+$/, '');

        setResult(formattedResult);
      }

      setShouldResetInput(true);
    } catch (error) {
      setResult('Error');
    }
  }, [input, safeEval]);

  // Handle keyboard events when calculator has focus
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Check if the key is a calculator key
      const isCalculatorKey = /[0-9]/.test(e.key) || ['+', '-', '*', '/', '.', '=', 'Enter', 'Escape', 'Delete', 'Backspace', '(', ')'].includes(e.key);

      // If it's a calculator key, prevent default and handle it
      if (isCalculatorKey) {
        e.preventDefault();
        e.stopPropagation();

        // Ensure calculator has focus
        if (calculatorRef.current) {
          calculatorRef.current.focus();
        }

        // Handle digit keys
        if (/[0-9]/.test(e.key)) {
          inputDigit(e.key);
        }
        // Handle operators
        else if (['+', '-', '*', '/'].includes(e.key)) {
          handleOperator(e.key);
        }
        // Handle decimal point
        else if (e.key === '.') {
          inputDecimal();
        }
        // Handle equals/enter
        else if (e.key === '=' || e.key === 'Enter') {
          performCalculation();
        }
        // Handle clearing (Escape or Delete)
        else if (e.key === 'Escape' || e.key === 'Delete') {
          clearInput();
        }
        // Handle backspace
        else if (e.key === 'Backspace') {
          handleBackspace();
        }
        // Handle parentheses
        else if (e.key === '(') {
          inputParenthesis('(');
        }
        else if (e.key === ')') {
          inputParenthesis(')');
        }
      }
    };

    // Add event listener with capture phase to intercept before other handlers
    document.addEventListener('keydown', handleKeyPress, true);
    return () => {
      document.removeEventListener('keydown', handleKeyPress, true);
    };
  }, [hasFocus, input, inputDigit, handleOperator, inputDecimal, performCalculation, clearInput, handleBackspace, inputParenthesis]);

  return (
    <div
      ref={calculatorRef}
      className="detachable-calculator"
      style={{
        position: 'fixed',
        top: `${position.y}px`,
        left: `${position.x}px`,
        zIndex: 1050,
        padding: '12px',
        backgroundColor: 'var(--card-bg, #fff)',
        borderRadius: '12px',
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
        border: '1px solid var(--border-color, #dee2e6)',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        width: '285px' // Fixed width to match calendar's rendered size
      }}
      onMouseDown={handleMouseDown}
      tabIndex="-1"
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="m-0">Calculator</h5>
        <Button 
          variant="link" 
          size="sm" 
          className="close-btn p-0 text-muted position-absolute"
          style={{ top: '10px', right: '10px' }}
          onClick={onClose}
          aria-label="Close calculator"
        >
          <i className="bi bi-x-circle-fill"></i>
        </Button>
      </div>
      
      {/* Input display */}
      <div 
        ref={inputRef}
        className={`calculator-input mb-1 ${hasFocus ? 'focused' : ''}`}
        style={{
          background: 'var(--bg-secondary, #f8f9fa)',
          border: '1px solid transparent',
          borderRadius: '4px',
          padding: '2px 4px',
          textAlign: 'right',
          fontSize: '1.25rem',
          fontFamily: 'monospace',
          minHeight: '20px',
          maxHeight: '40px',
          overflowX: 'auto',
          overflowY: 'auto',
          whiteSpace: 'nowrap',
          outline: 'none'
        }}
        onClick={() => calculatorRef.current?.focus()}
      >
        {input}
      </div>
      
      {/* Result display */}
      <div 
        className="calculator-result mb-2"
        style={{
          background: 'var(--bg-secondary, #f8f9fa)',
          border: '1px solid var(--border-color, #dee2e6)',
          borderRadius: '4px',
          padding: '4px 8px',
          textAlign: 'right',
          fontSize: '1.5rem',
          fontFamily: 'monospace',
          height: '44px',
          overflow: 'hidden',
          color: 'var(--bs-primary, #0d6efd)',
          fontWeight: 'bold'
        }}
      >
        {result || '0'}
      </div>
      
      <div className="calculator-keypad">
        <div className="row g-1 mb-1">
          <div className="col-3">
            <Button 
              variant="secondary" 
              className="w-100 calc-button"
              onClick={clearInput}
            >
              C
            </Button>
          </div>
          <div className="col-3">
            <Button 
              variant="outline-secondary" 
              className="w-100 calc-button"
              onClick={() => inputParenthesis('(')}
            >
              (
            </Button>
          </div>
          <div className="col-3">
            <Button 
              variant="outline-secondary" 
              className="w-100 calc-button"
              onClick={() => inputParenthesis(')')}
            >
              )
            </Button>
          </div>
          <div className="col-3">
            <Button 
              variant="outline-secondary" 
              className="w-100 calc-button"
              onClick={() => handleOperator('/')}
            >
              ÷
            </Button>
          </div>
        </div>
        
        <div className="row g-1 mb-1">
          <div className="col-3">
            <Button 
              variant="outline-secondary" 
              className="w-100 calc-button"
              onClick={() => inputDigit('7')}
            >
              7
            </Button>
          </div>
          <div className="col-3">
            <Button 
              variant="outline-secondary" 
              className="w-100 calc-button"
              onClick={() => inputDigit('8')}
            >
              8
            </Button>
          </div>
          <div className="col-3">
            <Button 
              variant="outline-secondary" 
              className="w-100 calc-button"
              onClick={() => inputDigit('9')}
            >
              9
            </Button>
          </div>
          <div className="col-3">
            <Button 
              variant="outline-secondary" 
              className="w-100 calc-button"
              onClick={() => handleOperator('*')}
            >
              ×
            </Button>
          </div>
        </div>
        
        <div className="row g-1 mb-1">
          <div className="col-3">
            <Button 
              variant="outline-secondary" 
              className="w-100 calc-button"
              onClick={() => inputDigit('4')}
            >
              4
            </Button>
          </div>
          <div className="col-3">
            <Button 
              variant="outline-secondary" 
              className="w-100 calc-button"
              onClick={() => inputDigit('5')}
            >
              5
            </Button>
          </div>
          <div className="col-3">
            <Button 
              variant="outline-secondary" 
              className="w-100 calc-button"
              onClick={() => inputDigit('6')}
            >
              6
            </Button>
          </div>
          <div className="col-3">
            <Button 
              variant="outline-secondary" 
              className="w-100 calc-button"
              onClick={() => handleOperator('-')}
            >
              −
            </Button>
          </div>
        </div>
        
        <div className="row g-1 mb-1">
          <div className="col-3">
            <Button 
              variant="outline-secondary" 
              className="w-100 calc-button"
              onClick={() => inputDigit('1')}
            >
              1
            </Button>
          </div>
          <div className="col-3">
            <Button 
              variant="outline-secondary" 
              className="w-100 calc-button"
              onClick={() => inputDigit('2')}
            >
              2
            </Button>
          </div>
          <div className="col-3">
            <Button 
              variant="outline-secondary" 
              className="w-100 calc-button"
              onClick={() => inputDigit('3')}
            >
              3
            </Button>
          </div>
          <div className="col-3">
            <Button 
              variant="outline-secondary" 
              className="w-100 calc-button"
              onClick={() => handleOperator('+')}
            >
              +
            </Button>
          </div>
        </div>
        
        <div className="row g-1">
          <div className="col-3">
            <Button 
              variant="outline-secondary" 
              className="w-100 calc-button"
              onClick={handleBackspace}
            >
              ←
            </Button>
          </div>
          <div className="col-3">
            <Button 
              variant="outline-secondary" 
              className="w-100 calc-button"
              onClick={() => inputDigit('0')}
            >
              0
            </Button>
          </div>
          <div className="col-3">
            <Button 
              variant="outline-secondary" 
              className="w-100 calc-button"
              onClick={inputDecimal}
            >
              .
            </Button>
          </div>
          <div className="col-3">
            <Button 
              variant="primary" 
              className="w-100 calc-button"
              onClick={performCalculation}
            >
              =
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetachableCalculator; 